import { FlexiSlider } from "appworks/graphics/elements/flexi-slider";
import { FlexiText } from "appworks/graphics/elements/flexi-text";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SliderControl } from "appworks/ui/controls/standard/slider/slider-control";
import { ValueList } from "appworks/utils/value-list";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { slotModel } from "slotworks/model/slot-model";

export abstract class AgsAbstractSliderControl extends SliderControl {

    protected sliderValues: ValueList<number>;
    protected onModelUpdateCallback: Function;
    protected addedZeroValue: boolean = false;

    /**
     * Constructs a new `AgsAbstractSliderControl` object.
     * @param slider - A `FlexiSlider` object representing the slider element.
     * @param label - A `FlexiText` object representing the text label element.
     */
    constructor(slider: FlexiSlider, label: FlexiText) {
        super(slider, label);

        this.slider.formatFunction = (index: number) => {
            return this.formatFunction(index);
        }

        this.onModelUpdateCallback = () => this.updateData();
        slotModel.onUpdate.add(this.onModelUpdateCallback);

        this.updateData();
    }

    /**
     * Destroys the `AgsAbstractSliderControl` object and cleans up any resources associated with it.
     */
    public override destroy(): void {
        super.destroy();
        slotModel.onUpdate.remove(this.onModelUpdateCallback);
    }

    /**
     * Set up the data for the slider.
     * @param array - An array of numeric values to use as slider options.
     * @param currentValue - The currently selected value from the array.
     */
    public override setupData(array: number[], currentValue: number): void {
        if (this.shouldShowZeroValueOption()) {
            // Always have an option for 0.
            if (array.length > 0 && array[0] !== 0) {
                this.addedZeroValue = true;
                array.unshift(0);
            }

            // Default to 0.
            if (!currentValue) {
                currentValue = 0;
            }
        }

        super.setupData(array, currentValue);
    }

     /**
     * Update the slider data based on the current model data.
     */
    protected updateData(): void {
        this.sliderValues = this.getModelData();

        if (this.sliderValues.values.length === 0) {
            this.setupData([0], 0);
            this.setEnabled(false);
        } else {
            this.setupData(this.sliderValues.values, this.sliderValues.currentValue);
        }
    }

    /**
     * Callback for when the slider is changed.
     * @param index - The index of the selected slider value.
     */
    protected override onChange(index: number): void {
        const modelData = this.getModelData();

        if (this.addedZeroValue) {
            index -= 1;
        }

        this.saveModelData(modelData.values[index]);
    }

    /**
     * Determine whether the zero value should be included as an option in the slider.
     */
    protected shouldShowZeroValueOption() {
        return true;
    }

    /**
     * Format the slider value as a currency string.
     * @param index - The index of the selected slider value.
     */
    protected formatFunction(index: number) {
        const value: number = this.sliderValues.values[index];
        return Services.get(CurrencyService).format(value, !gameiomModel.shouldDisplayCredits(), false);
    }

    /**
     * Get the current model data for the slider.
     */
    protected abstract getModelData(): ValueList<number>;

    /**
     * Save the current model data for the slider.
     */
    protected abstract saveModelData(selectedValue: number): void;
}
